import React from 'react';
import PropTypes from 'prop-types';
import {
  RepeatableField,
  TextField,
  Select,
  Row,
  Col,
  Label,
  IconButton,
} from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Pluggable } from '@folio/stripes/core';
import useTenantLocales from '../../hooks/useTenantLocales';

function RecipientsList({ input, meta, label }) {
  const intl = useIntl();
  const recipients = input.value || [];
  const { localeOptions } = useTenantLocales();

  const handleAdd = () => {
    // Default to Arabic locale for new recipients
    const defaultLocale = localeOptions.find(l => l.value === 'ar')?.value
      || localeOptions.find(l => l.value?.startsWith('ar'))?.value
      || localeOptions[0]?.value
      || 'ar';
    input.onChange([...recipients, { email: '', name: '', locale: defaultLocale }]);
  };

  const handleRemove = (index) => {
    input.onChange(recipients.filter((_, i) => i !== index));
  };

  const updateRecipient = (index, field, value) => {
    const updated = [...recipients];
    updated[index] = { ...updated[index], [field]: value };
    input.onChange(updated);
  };

  const fillFromPlugin = (index, user) => {
    const email = user?.personal?.email || '';
    const name  = [user?.personal?.firstName, user?.personal?.lastName]
      .filter(Boolean).join(' ') || user?.username || '';
    const updated = [...recipients];
    updated[index] = { ...updated[index], email, name };
    input.onChange(updated);
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const headLabels = (
    <Row>
      <Col xs={4}>
        <Label>
          <FormattedMessage id="ui-reports.recipients.name" defaultMessage="Name" />
        </Label>
      </Col>
      <Col xs={4}>
        <Label required>
          <FormattedMessage id="ui-reports.recipients.email" defaultMessage="Email" />
        </Label>
      </Col>
      <Col xs={3}>
        <Label>
          <FormattedMessage id="ui-reports.recipients.locale" defaultMessage="Language" />
        </Label>
      </Col>
    </Row>
  );

  return (
    <div>
      {label && <Label required>{label}</Label>}

      <RepeatableField
        fields={recipients}
        headLabels={headLabels}
        onAdd={handleAdd}
        onRemove={handleRemove}
        addLabel={
          <FormattedMessage id="ui-reports.recipients.add" defaultMessage="Add Recipient" />
        }
        renderField={(field, index) => (
          <Row style={{ marginBottom: '0.5rem' }}>
            <Col xs={4}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ marginTop: '-0.5rem', flexShrink: 0 }}>
                  <Pluggable
                    type="find-user"
                    selectUser={(user) => fillFromPlugin(index, user)}
                    visibleColumns={['name', 'username', 'email', 'barcode']}
                    renderTrigger={(props) => (
                      <IconButton
                        {...props}
                        icon="search"
                        ariaLabel={intl.formatMessage({
                          id: 'ui-reports.recipients.findUser',
                          defaultMessage: 'Find User',
                        })}
                      />
                    )}
                  />
                </div>
                <TextField
                  value={recipients[index]?.name || ''}
                  onChange={(e) => updateRecipient(index, 'name', e.target.value)}
                  placeholder={intl.formatMessage({
                    id: 'ui-reports.recipients.namePlaceholder',
                    defaultMessage: 'Recipient name',
                  })}
                  fullWidth
                  marginBottom0
                />
              </div>
            </Col>
            <Col xs={4}>
              <TextField
                value={recipients[index]?.email || ''}
                onChange={(e) => updateRecipient(index, 'email', e.target.value)}
                placeholder={intl.formatMessage({
                  id: 'ui-reports.recipients.emailPlaceholder',
                  defaultMessage: 'Recipient email address',
                })}
                required
                error={
                  !recipients[index]?.email
                    ? intl.formatMessage({ id: 'ui-reports.recipients.emailRequired', defaultMessage: 'Email is required' })
                    : !validateEmail(recipients[index]?.email)
                    ? intl.formatMessage({ id: 'ui-reports.recipients.invalidEmail', defaultMessage: 'Invalid email' })
                    : null
                }
                fullWidth
                marginBottom0
              />
            </Col>
            <Col xs={3}>
              <Select
                value={recipients[index]?.locale || localeOptions[0]?.value || 'ar'}
                onChange={(e) => updateRecipient(index, 'locale', e.target.value)}
                dataOptions={localeOptions}
                marginBottom0
              />
            </Col>
          </Row>
        )}
      />

      {meta.touched && meta.error && (
        <div style={{ color: 'red', fontSize: '0.85rem', marginTop: '0.25rem' }}>
          {meta.error}
        </div>
      )}
    </div>
  );
}

RecipientsList.propTypes = {
  input: PropTypes.object.isRequired,
  meta:  PropTypes.object.isRequired,
  label: PropTypes.node,
};

export default RecipientsList;
